import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { ShoppingCart, Zap } from "lucide-react";
import { accessoryGroups } from "@/lib/accessoriesCatalog";
import { useCart, formatUSD } from "@/contexts/CartContext";
import { toast } from "@/hooks/use-toast";
import { useMemo, useState } from "react";

const Accessories = () => {
  const { addItem, buyNow, openCart } = useCart();
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const categories = useMemo(
    () => ["All", ...accessoryGroups.map((g) => g.category)],
    []
  );

  const visibleGroups = useMemo(
    () =>
      activeCategory === "All"
        ? accessoryGroups
        : accessoryGroups.filter((g) => g.category === activeCategory),
    [activeCategory]
  );

  return (
    <div className="min-h-screen">
      <Navigation />

      <main className="container mx-auto px-4 pt-32 md:pt-40 pb-12">
        {/* Hero */}
        <section className="text-center mb-12">
          <h1 className="font-orbitron font-bold text-4xl md:text-5xl mb-4">
            Robot <span className="text-primary">Accessories</span> & Parts
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-6">
            Genuine PUDU accessories, attachments, and replacement parts. Grouped by robot
            family so you can find exactly what fits your fleet.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
              onClick={openCart}
            >
              <ShoppingCart className="h-4 w-4 mr-2" /> View Cart
            </Button>
            <Button asChild size="lg" variant="outline" className="font-bold">
              <a href="tel:+13369624539">Call to Order: (336) 962-4539</a>
            </Button>
          </div>
        </section>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          {categories.map((c) => (
            <Button
              key={c}
              size="sm"
              variant={activeCategory === c ? "default" : "outline"}
              className="rounded-full"
              onClick={() => setActiveCategory(c)}
            >
              {c}
            </Button>
          ))}
        </div>

        {/* Grouped accessories */}
        {visibleGroups.map((group) => (
          <section key={group.category} className="mb-16">
            <div className="mb-6">
              <h2 className="font-orbitron font-bold text-2xl md:text-3xl">
                <span className="text-primary">{group.category}</span>
              </h2>
              {group.description && (
                <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {group.products.map((p) => (
                <Card
                  key={p.id}
                  className="border-[hsl(var(--line))] glass-effect p-6 hover:bg-white/10 transition-all duration-300 flex flex-col"
                >
                  <div className="mb-4 rounded-lg overflow-hidden bg-white/5 flex items-center justify-center" style={{ height: "200px" }}>
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-contain p-4"
                      loading="lazy"
                    />
                  </div>
                  <div className="mb-2">
                    <span className="text-xs text-primary font-bold uppercase tracking-wider">
                      {group.category}
                    </span>
                  </div>
                  <h3 className="font-orbitron font-bold text-lg mb-2 leading-snug">{p.name}</h3>
                  {p.compatibleWith && (
                    <p className="text-xs text-muted-foreground mb-3">
                      <span className="font-semibold">Compatible:</span> {p.compatibleWith}
                    </p>
                  )}
                  <div className="mt-auto pt-3 border-t border-[hsl(var(--line))]">
                    <div className="text-2xl font-bold text-primary mb-3">{formatUSD(p.price)}</div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          addItem({
                            id: p.id,
                            name: p.name,
                            price: p.price,
                            image: p.image,
                            category: group.category,
                          });
                          toast({
                            title: "Added to cart",
                            description: p.name,
                          });
                        }}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
                      </Button>
                      <Button
                        className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                        onClick={() =>
                          buyNow({
                            id: p.id,
                            name: p.name,
                            price: p.price,
                            image: p.image,
                            category: group.category,
                          })
                        }
                      >
                        <Zap className="h-4 w-4 mr-2" /> Buy Now
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        ))}

        {/* Custom Solutions CTA */}
        <section className="text-center">
          <div className="border border-[hsl(var(--line))] rounded-xl p-8 md:p-12 glass-effect">
            <h2 className="font-orbitron font-bold text-3xl mb-4">
              Need <span className="text-primary">Custom Parts</span>?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Can't find what you're looking for? We can source custom attachments and accessories
              tailored to your specific needs. Contact our team for specialized solutions.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
                <a href="tel:+13369624539">Call Us</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="font-bold">
                <a href="mailto:neilai@neilaiglobalsolutionsllc.com">Email Us</a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Accessories;
